import { IconTexture } from '@tabler/icons-react';
import { useMemo, useRef, useState, useEffect } from 'react';
import ImagePreview from './ImagePreview';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { useDynamicPopupPosition } from '@/hooks/useDynamicPopupPosition';

export default function ImagePicker({
  defaultImagesUrls = [],
  defaultImagesLabel,
  selectedImage,
  uploadedImagesUrls = [],
  children = <></>, // empty state for default images
  onDelete,
  onImagesUploaded,
  onImageSelected
}: {
  defaultImagesUrls: string[];
  defaultImagesLabel: string;
  selectedImage?: string | undefined;
  uploadedImagesUrls: string[];
  children?: React.ReactNode;
  onImagesUploaded: (files: File[]) => any;
  onDelete: (imageUrl) => void;
  onImageSelected: (url: string) => void;
}) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const uploadedImages = useMemo(() => {
    if (!!selectedImage && !uploadedImagesUrls.includes(selectedImage) && !defaultImagesUrls.includes(selectedImage)) {
      // If selected image is not in the lists of images - show it in uploaded list
      return [...uploadedImagesUrls, selectedImage];
    }
    return uploadedImagesUrls;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImagesUrls]);

  const { position, isPositioned } = useDynamicPopupPosition({
    triggerRef,
    popupRef,
    isVisible: isOpen,
    preferredPosition: 'right',
    offset: 8,
    viewportPadding: 16
  });

  // Handle click outside to close
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;

        // Don't close if clicking on confirmation dialogs
        const confirmationDialog = document.querySelector('[role="dialog"]');
        if (confirmationDialog && confirmationDialog.contains(target)) {
          return;
        }

        // Don't close if clicking on delete buttons or their containers
        const deleteButton = target.closest('[data-testid*="delete"]');
        if (deleteButton) {
          return;
        }

        // Don't close if clicking on any button elements (including IconButton)
        const buttonElement = target.closest('button');
        if (buttonElement) {
          return;
        }

        // Close if clicking outside the popup and trigger
        if (
          popupRef.current &&
          !popupRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative size-full">
      <div className="flex size-full items-center justify-center">
        <div
          ref={triggerRef}
          data-testid="image-picker-button"
          className="flex size-full cursor-pointer items-center justify-center overflow-hidden rounded-full border border-slate-300 bg-slate-100 focus:outline-none focus:ring-0"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selectedImage ? (
            <div className="size-full bg-cover bg-center" style={{ backgroundImage: `url(${selectedImage})` }} />
          ) : (
            <div className="h-full w-px rotate-45 bg-slate-300" />
          )}
        </div>
      </div>
      {isOpen && (
        <div
          ref={popupRef}
          className="fixed z-30 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
          style={{
            ...position,
            opacity: isPositioned ? 1 : 0,
            transition: 'opacity 150ms ease-in-out'
          }}
        >
          <div className="flex items-center justify-center p-2" onClick={e => e.stopPropagation()}>
            {!uploadedImages.length ? (
              <FilesInput
                onFilesUpload={onImagesUploaded}
                accept={'.jpeg,.png'}
                label="Upload a file"
                multiple={false}
                icon="IconUpload"
              />
            ) : (
              <div className="flex w-full flex-wrap gap-1">
                {uploadedImages.map(image => {
                  return (
                    <ImagePreview
                      key={image}
                      imageUrl={image}
                      isSelected={selectedImage === image}
                      onDelete={() => onDelete(image)}
                      onImageSelected={onImageSelected}
                    />
                  );
                })}
                <div className="size-14">
                  <FilesInput
                    onFilesUpload={onImagesUploaded}
                    accept={'.jpeg,.png'}
                    label=""
                    multiple={false}
                    icon="IconUpload"
                    containerClassNames="!p-3"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="relative mt-4 flex items-center justify-center border-t pb-1.5 pl-2 pt-2.5">
            <div className="absolute -top-3.5 flex items-center space-x-0.5 rounded-full border bg-white px-3 py-1">
              <IconTexture size={16} />
              <span className="text-xs">{defaultImagesLabel}</span>
            </div>
            <div className="mt-2 flex max-h-28 w-full flex-wrap gap-1 overflow-y-scroll">
              {defaultImagesUrls.length
                ? defaultImagesUrls.map(image => {
                    return (
                      <ImagePreview
                        key={image}
                        imageUrl={image}
                        isSelected={selectedImage === image}
                        onImageSelected={onImageSelected}
                      />
                    );
                  })
                : children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
